"use client";

import { useCallback, useEffect, useState } from "react";
import {
  CART_STORAGE_KEY,
  type CheckoutCart,
  type MailBilling,
  parseCart,
} from "@/lib/checkout";

const EMPTY: CheckoutCart = {
  moduleIds: [],
  mailboxQty: 0,
  mailBilling: "monthly",
};

const CART_SYNC_EVENT = "jdi:checkout-cart";

function readStoredCart(): CheckoutCart {
  try {
    const raw = sessionStorage.getItem(CART_STORAGE_KEY);
    if (raw) {
      const parsed = parseCart(JSON.parse(raw));
      if (parsed) return parsed;
    }
  } catch {
    /* ignore */
  }
  return EMPTY;
}

export function useCheckoutCart() {
  const [cart, setCartState] = useState<CheckoutCart>(EMPTY);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setCartState(readStoredCart());
    setReady(true);

    function sync() {
      setCartState(readStoredCart());
    }

    window.addEventListener(CART_SYNC_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(CART_SYNC_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const persist = useCallback((next: CheckoutCart) => {
    setCartState(next);
    try {
      sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify(next));
      window.dispatchEvent(new Event(CART_SYNC_EVENT));
    } catch {
      /* ignore */
    }
  }, []);

  const addModule = useCallback(
    (moduleId: string) => {
      persist({
        ...cart,
        moduleIds: cart.moduleIds.includes(moduleId)
          ? cart.moduleIds
          : [...cart.moduleIds, moduleId],
      });
    },
    [cart, persist],
  );

  const removeModule = useCallback(
    (moduleId: string) => {
      persist({
        ...cart,
        moduleIds: cart.moduleIds.filter((id) => id !== moduleId),
      });
    },
    [cart, persist],
  );

  const setMailboxes = useCallback(
    (qty: number, billing?: MailBilling) => {
      persist({
        ...cart,
        mailboxQty: Math.max(0, Math.min(500, qty)),
        mailBilling: billing ?? cart.mailBilling,
      });
    },
    [cart, persist],
  );

  const updateContact = useCallback(
    (contactEmail: string, orgName?: string) => {
      persist({ ...cart, contactEmail, orgName: orgName ?? cart.orgName });
    },
    [cart, persist],
  );

  const clearCart = useCallback(() => persist(EMPTY), [persist]);

  const itemCount =
    cart.moduleIds.length + (cart.mailboxQty > 0 ? 1 : 0);

  return {
    cart,
    ready,
    itemCount,
    addModule,
    removeModule,
    setMailboxes,
    updateContact,
    clearCart,
    setCart: persist,
  };
}
